import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import gsap from "gsap";
import NavbarNew from "../components/common/NavbarNew";

const stats = [
  { id: "students", target: 1000, label: "Students", suffix: "+" },
  { id: "courses", target: 10, label: "Courses", suffix: "+" },
  { id: "faculty", target: 50, label: "Expert Faculty", suffix: "+" },
  { id: "success", target: 98, label: "Success Rate", suffix: "%" },
];

const HomeNew = () => {
  const { user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [counters, setCounters] = useState({
    students: 0,
    courses: 0,
    faculty: 0,
    success: 0,
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const statsRef = useRef(null);
  const coursesRef = useRef(null);

  const slides = [
    {
      image: "/banner1.jpeg",
      title: "Welcome to Excellence in Education",
      subtitle: "Empowering minds, shaping futures",
    },
    {
      image: "/banner.jpeg",
      title: "Learn From Expert Faculty",
      subtitle: "World-class education at your fingertips",
    },
    {
      image: "/banner3.jpeg",
      title: "Build Your Future Today",
      subtitle: "Join thousands of successful students",
    },
    {
      image: "/banner5.jpeg",
      title: "Innovation Meets Education",
      subtitle: "Modern learning for the modern world",
    },
  ];

  useEffect(() => {
    // Auto-rotate carousel
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    // Hero section animation
    if (heroRef.current) {
      gsap.from(heroRef.current.children, {
        opacity: 0,
        y: 50,
        duration: 1,
        stagger: 0.2,
        ease: "power3.out",
      });
    }

    // Features animation on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.fromTo(
              entry.target.children,
              { opacity: 0, y: 30 },
              {
                opacity: 1,
                y: 0,
                duration: 0.8,
                stagger: 0.15,
                ease: "power2.out",
              }
            );

            // Animate counters when stats section is visible
            if (entry.target === statsRef.current && !hasAnimated) {
              setHasAnimated(true);
              stats.forEach((stat) => {
                animateCounter(stat.id, stat.target);
              });
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    if (featuresRef.current) observer.observe(featuresRef.current);
    if (statsRef.current) observer.observe(statsRef.current);
    if (coursesRef.current) observer.observe(coursesRef.current);

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounter = (id, target) => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      setCounters((prev) => ({ ...prev, [id]: Math.floor(current) }));
    }, duration / steps);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const courses = [
    {
      name: "Islamiat",
      description: "Islamic Studies and Religious Education",
      // icon: "🕌",
      color: "from-green-500 to-emerald-60",
      image:
        "https://images.unsplash.com/photo-1572358899655-f63ece97bfa5?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8bWFkaW5haHxlbnwwfHwwfHx8MA%3D%3D",
    },
    {
      name: "English",
      description: "Language Arts and Communication Skills",
      icon: "📚",
      color: "from-blue-500 to-cyan-600",
      image:
        "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400&h=300&fit=crop",
    },
    {
      name: "Biology",
      description: "Life Sciences and Natural Studies",
      icon: "🧬",
      color: "from-green-600 to-teal-600",
      image:
        "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&h=300&fit=crop",
    },
    {
      name: "Physics",
      description: "Physical Sciences and Mechanics",
      icon: "⚡",
      color: "from-purple-500 to-indigo-600",
      image:
        "https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?w=400&h=300&fit=crop",
    },
    {
      name: "Mathematics",
      description: "Pure and Applied Mathematics",
      icon: "📐",
      color: "from-red-500 to-pink-600",
      image:
        "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=300&fit=crop",
    },
    {
      name: "Urdu",
      description: "Urdu Language and Literature",
      icon: "✍️",
      color: "from-amber-500 to-orange-600",
      image:
        "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=400&h=300&fit=crop",
    },
    {
      name: "Sindhi",
      description: "Sindhi Language and Culture",
      icon: "📖",
      color: "from-yellow-500 to-amber-600",
      image:
        "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=300&fit=crop",
    },
    {
      name: "Computer Science",
      description: "Programming and Technology",
      icon: "💻",
      color: "from-blue-600 to-indigo-700",
      image:
        "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=300&fit=crop",
    },
  ];

  const features = [
    {
      icon: (
        <svg
          className="h-16 w-16"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      title: "World-Class Faculty",
      description:
        "Learn from PhD holders and industry experts with 15+ years of teaching excellence. Our educators bring real-world experience to every lesson.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: (
        <svg
          className="h-16 w-16"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
      title: "Advanced Curriculum",
      description:
        "Industry-aligned programs updated quarterly to match global standards. Hands-on projects, case studies, and practical assignments.",
      gradient: "from-green-500 to-emerald-500",
    },
    {
      icon: (
        <svg
          className="h-16 w-16"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      title: "Flexible Learning",
      description:
        "Study anywhere, anytime with our mobile-friendly platform. Downloadable resources, recorded lectures, and lifetime course access.",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      icon: (
        <svg
          className="h-16 w-16"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
          />
        </svg>
      ),
      title: "Accredited Certification",
      description:
        "Globally recognized certificates upon completion. Boost your resume with industry-validated credentials and skill badges.",
      gradient: "from-orange-500 to-red-500",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <NavbarNew />

      {/* Hero Section with Carousel */}
      <div className="relative h-screen mt-16 overflow-hidden">
        {/* Carousel Slides */}
        <div className="relative h-full">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${slide.image})`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-blue-900/70 to-transparent"></div>
              </div>

              <div className="relative h-full flex items-center">
                <div
                  className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full"
                  ref={index === 0 ? heroRef : null}
                >
                  <div className="max-w-3xl">
                    <h1
                      className="text-5xl md:text-7xl font-bold mb-6 leading-tight drop-shadow-2xl"
                      style={{
                        color: index === currentSlide ? "white" : "white",
                        textShadow: "2px 2px 8px rgba(0,0,0,0.8)",
                      }}
                    >
                      {slide.title}
                    </h1>
                    <p
                      className="text-xl md:text-2xl mb-8 drop-shadow-lg"
                      style={{
                        color:
                          index === currentSlide
                            ? "white"
                            : "rgba(255, 255, 255, 0.98)",
                        textShadow: "1px 1px 4px rgba(0,0,0,0.7)",
                      }}
                    >
                      {slide.subtitle}
                    </p>
                    <div className="flex flex-wrap gap-4">
                      {user ? (
                        <Link
                          to={`/${user.role}`}
                          className="px-8 py-4 bg-green-600 text-white rounded-lg font-semibold text-lg hover:bg-green-700 hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                        >
                          Go to Dashboard
                        </Link>
                      ) : (
                        <>
                          <Link
                            to="/register"
                            className="px-8 py-4 bg-green-600 text-white rounded-lg font-semibold text-lg hover:bg-green-700 hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                          >
                            Enroll Now
                          </Link>
                          <Link
                            to="/courses"
                            className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-lg font-semibold text-lg hover:bg-white/30 transition-all duration-300 transform hover:scale-110"
                          >
                            Explore Courses
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-3 rounded-full transition-all duration-300 z-10 transform hover:scale-110 hover:shadow-xl"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-3 rounded-full transition-all duration-300 z-10 transform hover:scale-110 hover:shadow-xl"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Carousel Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 z-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-3 rounded-full transition-all duration-300 transform hover:scale-125 ${
                index === currentSlide
                  ? "w-12 bg-white shadow-lg"
                  : "w-3 bg-white/50 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Courses Section */}
      <div className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Our Courses
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Explore our comprehensive range of subjects
            </p>
          </div>

          <div
            ref={coursesRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {courses.map((course, index) => (
              <div key={index} className="group perspective-1000 h-80">
                <div className="relative w-full h-full transition-transform duration-700 transform-style-3d group-hover:rotate-y-180">
                  {/* Front Side */}
                  <div className="absolute inset-0 backface-hidden rounded-2xl shadow-2xl hover:shadow-red-300/50 dark:hover:shadow-gray-400/50 transition-shadow duration-300 overflow-hidden">
                    <div className="relative h-full">
                      <img
                        src={course.image}
                        alt={course.name}
                        className="w-full h-full object-cover"
                      />
                      <div
                        className={`absolute inset-0 bg-gradient-to-t ${course.color} opacity-85`}
                      ></div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
                        <span className="text-7xl mb-4">{course.icon}</span>
                        <h3 className="text-2xl font-bold text-center">
                          {course.name}
                        </h3>
                      </div>
                    </div>
                  </div>

                  {/* Back Side */}
                  <div
                    className={`absolute inset-0 backface-hidden rounded-2xl shadow-2xl hover:shadow-red-300/50 dark:hover:shadow-gray-400/50 transition-shadow duration-300 bg-gradient-to-br ${course.color} rotate-y-180 p-6 flex flex-col justify-between text-white`}
                  >
                    <div>
                      <h3 className="text-2xl font-bold mb-4">{course.name}</h3>
                      <p className="text-sm mb-4 leading-relaxed">
                        {course.description}
                      </p>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-center">
                          <svg
                            className="h-4 w-4 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Expert Instructors
                        </li>
                        <li className="flex items-center">
                          <svg
                            className="h-4 w-4 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Interactive Lessons
                        </li>
                        <li className="flex items-center">
                          <svg
                            className="h-4 w-4 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Certification Available
                        </li>
                      </ul>
                    </div>
                    <Link
                      to="/courses"
                      className="mt-4 px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold text-center hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
                    >
                      Enroll Now
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-20 bg-gradient-to-r from-green-400 to-blue-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            ref={statsRef}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-black"
          >
            {stats.map((stat) => (
              <div
                key={stat.id}
                className="transform hover:scale-110 transition-transform duration-300"
              >
                <div className="text-5xl font-bold mb-2">
                  {counters[stat.id]}
                  {stat.suffix}
                </div>
                <div className="text-xl text-green-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose Us
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Discover what makes us the preferred choice for thousands of
              students worldwide. Excellence, innovation, and success await you.
            </p>
          </div>
          <div
            ref={featuresRef}
            className="grid grid-cols-1 md:grid-cols-2 gap-10"
          >
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group relative z-0 bg-gradient-to-br ${feature.gradient} p-8 rounded-3xl shadow-2xl hover:shadow-red-400/60 dark:hover:shadow-gray-500/60 transform hover:-translate-y-3 transition-all duration-500 overflow-hidden`}
              >
                {/* Background Pattern */}
                <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
                  <div className="absolute inset-0 bg-gray-500 transform rotate-45 scale-150"></div>
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <div className="text-white mb-6 group-hover:scale-110 transition-transform duration-500 inline-block">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-white/90 leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {/* Hover Glow */}
                <div className="absolute inset-0 z-20 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gray-200 shadow-slate-500 dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Ready to Start Learning?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Join thousands of students already learning on our platform
          </p>
          {!user && (
            <Link
              to="/register"
              className="inline-block px-12 py-4 bg-green-600 text-white rounded-lg font-semibold text-lg hover:bg-green-700 hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              Enroll Now
            </Link>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-700 via-gray-500 to-gray-700 text-white py-12 transition-colors duration-300 border-t-4 border-green-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center md:text-left">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center justify-center md:justify-start space-x-3 mb-4">
                <img
                  src="/gTlogo.jpeg"
                  alt="School Logo"
                  className="h-12 w-12 rounded-full"
                />
                <h3 className="text-xl font-bold">School LMS</h3>
              </div>
              <p className="text-gray-300">
                Empowering education through technology
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-300">
                <li>
                  <Link
                    to="/courses"
                    className="hover:text-green-400 transition-all duration-300 transform inline-block hover:translate-x-1"
                  >
                    Courses
                  </Link>
                </li>
                <li>
                  <Link
                    to="/programmes"
                    className="hover:text-green-400 transition-all duration-300 transform inline-block hover:translate-x-1"
                  >
                    Programmes
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faculty"
                    className="hover:text-green-400 transition-all duration-300 transform inline-block hover:translate-x-1"
                  >
                    Faculty
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-300">
                <li>
                  <Link
                    to="/testimonials"
                    className="hover:text-green-400 transition-all duration-300 transform inline-block hover:translate-x-1"
                  >
                    Testimonials
                  </Link>
                </li>
                <li>
                  <Link
                    to="/gallery"
                    className="hover:text-green-400 transition-all duration-300 transform inline-block hover:translate-x-1"
                  >
                    Gallery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="hover:text-green-400 transition-all duration-300 transform inline-block hover:translate-x-1"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div className="col-span-2 md:col-span-1">
              <h4 className="font-bold mb-4">Connect With Us</h4>
              <div className="flex justify-center md:justify-start space-x-4">
                {/* Facebook */}
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 p-2 rounded-full transition-all duration-300 transform hover:scale-125 hover:-translate-y-1 shadow-lg"
                  aria-label="Facebook"
                >
                  <svg
                    className="h-6 w-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                {/* X (Twitter) */}
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black hover:bg-gray-800 p-2 rounded-full transition-all duration-300 transform hover:scale-125 hover:-translate-y-1 shadow-lg"
                  aria-label="X (Twitter)"
                >
                  <svg
                    className="h-6 w-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                {/* LinkedIn */}
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-700 hover:bg-blue-800 p-2 rounded-full transition-all duration-300 transform hover:scale-125 hover:-translate-y-1 shadow-lg"
                  aria-label="LinkedIn"
                >
                  <svg
                    className="h-6 w-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                {/* WhatsApp */}
                <a
                  href="https://wa.me/1234567890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 hover:bg-green-600 p-2 rounded-full transition-all duration-300 transform hover:scale-125 hover:-translate-y-1 shadow-lg"
                  aria-label="WhatsApp"
                >
                  <svg
                    className="h-6 w-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-400 text-center text-gray-100">
            <p>&copy; 2025 School LMS. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeNew;
